import React, { useEffect } from 'react';
import { Megaphone, X, CheckCircle2 } from 'lucide-react';
import useAnnouncementPrompt from '../../hooks/useAnnouncementPrompt';

/**
 * Dismissible announcement banner — mounted once in the app shell.
 * Renders nothing unless the backend serves an active announcement
 * targeting the current user (audience-aware).
 */
const AnnouncementPrompt = () => {
  const {
    announcement,
    isOpen,
    isSubmitting,
    showThanks,
    submitResponse,
    dismissPrompt,
  } = useAnnouncementPrompt();

  // Keyboard: Escape dismisses (when dismissible).
  useEffect(() => {
    if (!isOpen || !announcement?.is_dismissible) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') dismissPrompt('esc');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, announcement?.is_dismissible, dismissPrompt]);

  if (!isOpen || !announcement) return null;

  const dismissible = announcement.is_dismissible !== false;

  return (
    <div
      className="fixed inset-x-0 bottom-4 z-[70] flex justify-center px-4 sm:bottom-6"
      role="dialog"
      aria-live="polite"
      aria-label={announcement.title}
    >
      <div className="w-full max-w-md rounded-3xl border border-glass bg-[#14141f]/95 px-5 py-4 text-sm text-white/85 shadow-[0_18px_50px_rgba(10,10,26,0.55)] backdrop-blur-xl">
        {showThanks ? (
          <div className="flex items-center gap-2.5 py-1 text-sm">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
            <span>Thanks — noted.</span>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/50">
                <Megaphone className="h-3.5 w-3.5" />
                Announcement
              </div>
              {dismissible && (
                <button
                  type="button"
                  onClick={() => dismissPrompt('close')}
                  disabled={isSubmitting}
                  className="shrink-0 rounded-lg p-1 text-white/40 transition hover:bg-white/10 hover:text-white"
                  aria-label="Dismiss announcement"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="mt-2 text-sm font-semibold leading-tight text-white">
              {announcement.title}
            </div>
            {announcement.body ? (
              <div className="mt-1.5 text-xs leading-relaxed text-white/65">
                {announcement.body}
              </div>
            ) : null}

            <div className="mt-4 flex items-center gap-2">
              {announcement.cta_label ? (
                <button
                  type="button"
                  onClick={() => submitResponse('')}
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-semibold text-black transition hover:bg-white/90 disabled:opacity-60"
                >
                  {announcement.cta_label}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => submitResponse('')}
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-semibold text-black transition hover:bg-white/90 disabled:opacity-60"
                >
                  Got it
                </button>
              )}
              {dismissible && (
                <button
                  type="button"
                  onClick={() => dismissPrompt('not_now')}
                  disabled={isSubmitting}
                  className="rounded-full px-4 py-2 text-xs font-medium text-white/55 transition hover:bg-white/10 hover:text-white disabled:opacity-60"
                >
                  Not now
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnnouncementPrompt;
